import { Suspense, useEffect, useMemo, useState } from 'react';
import { useLazyLoadQuery, graphql, fetchQuery, useRelayEnvironment } from 'react-relay';
import { DataGrid, GridCellParams, GridPaginationModel, GridRowHeightParams, GridRowId, GridRowSelectionModel, GridSortModel, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useInsightSearchQueries } from '../../hooks/useGlobalSearchQueries';
import { useGlobalState } from '../../../../state/v2/store';
import { useInsightsFilterMenuParams } from '../FilterMenu/useFilterMenuParams'
import { InsightsTableQuery, InsightsTableQuery$variables, KPI } from './__generated__/InsightsTableQuery.graphql';
import { KpiDisplay, getDollarFormat, getInsightHeaderName, serializeInsightRate } from '../../../../display';
import { KPI as PrismaKPI } from '@prisma/client';
import { InsightDrawer } from '../InsightDrawer/InsightDrawer';
import { Drawer } from '@mui/material';
import { InsightsTableExportCSVQuery } from './__generated__/InsightsTableExportCSVQuery.graphql';
import { Button } from '../../../../components/Button/Button';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatEnumString } from '../../../../common';
import { CurrencyType } from '../TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql';
import { FilterBar } from '../FilterBar/FilterBar';
import { LoadingScreen } from '../LoadingScreen/LoadingScreen';
import { HeaderWithTooltip } from '../HeaderWithTooltip/HeaderWithTooltip';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import { useSafeLabelLiftQueryQuery$data } from '../InsightDrawer/__generated__/useSafeLabelLiftQueryQuery.graphql';
import { LabelLiftData } from '../InsightDrawer/useSafeLabelLiftQuery';
import { InsightDrawerWithCachedData } from '../InsightDrawer/InsightDrawerWithCachedData';
import { InsightDrawerWithQuery } from '../InsightDrawer/InsightDrawerWithQuery';
import { useTrackingContext } from '../../../../analytics/useTracking';
import { trackInsightDrawerOpened, trackInsightsExported, trackShareInsight } from '../../../../analytics/tracking';
import type { LabelType, ShareInsightSource } from '../../../../analytics/types';
import { RowActionsButton, RowActionsMenu } from './RowActionsMenu';
import { DownloadInsightModal } from '../DownloadInsightModal/DownloadInsightModal';

export interface Insight {
    id: string
    labelName: string | null
    labelType: string | null
    rawLabelType: string | null
    labelLift: string | null
    rateWhenAbsent: string | null
    rateWhenPresent: string | null
    adSpendPresent: string | null
    adSpendAbsent: string | null
    countPresent: string | null
    countAbsent: string | null
    contentUrls: (string | null)[] | null
    contentTexts: (string | null)[] | null
    metaAds: (string | null)[] | null
}


const TextCell: React.FC<{ value: string }> = ({ value }) => {
    return (
      <div
        style={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            paddingLeft: '0px'
        }}
      >
        {value}
      </div>
    );
  };
  
interface InsightsTableProps {
  insightsGraphEnabled: boolean;
  metricInsightGraphEnabled?: boolean;
}

const InsightsTable = ({ insightsGraphEnabled, metricInsightGraphEnabled = false }: InsightsTableProps) => {
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'labelLift', sort: 'desc' }]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null)
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
    // Which row's ⋯ menu is open, and the button it should anchor to.
    const [rowMenu, setRowMenu] = useState<{ rowId: string; anchorEl: HTMLElement } | null>(null);
    const [downloadInsight, setDownloadInsight] = useState<Insight | null>(null);
    const [insights, setInsights] = useState<Insight[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [insightsCurrencyType, setInsightsCurrencyType] = useState<CurrencyType>('USD');
    const [{ performanceDateGte, performanceDateLte, insightKpi, labelType, insightMetricsFilters }, setParams] =
        useInsightsFilterMenuParams()

    const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(","))
    const workspaceName = useGlobalState((b) => b.brand?.[0]?.name) ?? ''
    const ctx = useTrackingContext()

    const searchQueries = useInsightSearchQueries()
    const environment = useRelayEnvironment()
    const camelToSnake = (str: string) => {
        return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).toUpperCase();
    };
    const tableQueryVariables = useMemo(() => {
        let sortOrder: string;
        if (sortModel.length === 1) {
            sortOrder = `${camelToSnake(sortModel[0].field)}_${(sortModel[0].sort || "asc").toUpperCase()}`;
        } else {
            sortOrder = 'LABEL_LIFT_DESC';
        }
        return {
            skip: !brandId,
            input: {
                pagination: {
                    skip: paginationModel.page * paginationModel.pageSize,
                    take: paginationModel.pageSize,
                },
                filter: {
                    brandId: brandId!,
                    searchQueries,
                    kpi: insightKpi as KPI,
                    labelTypes: labelType,
                    metrics: insightMetricsFilters,
                    performanceDateRange:
                        performanceDateGte && performanceDateLte
                            ? {
                                start: performanceDateGte,
                                end: performanceDateLte,
                            }
                            : undefined,
                },
                orderBy: sortOrder,
            },
        } as InsightsTableQuery$variables;
    }, [performanceDateGte, performanceDateLte, insightMetricsFilters, brandId, paginationModel, insightKpi, searchQueries, labelType, sortModel]);
    
    const res = useLazyLoadQuery<InsightsTableQuery>(
        graphql`
      query InsightsTableQuery($input: ListInsightV2Request!, $skip: Boolean!) {
        listInsightV2(input: $input) @skip(if: $skip) {
          count
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          metadata {
            currencyType
          }
          data {
            id
            labelName
            labelType
            labelLift
            rateWhenAbsent
            rateWhenPresent
            adSpendPresent
            adSpendAbsent
            numPresent
            numAbsent
            contentUrls
            contentTexts
            metaAds
          }
        }
      }
    `, tableQueryVariables, { fetchPolicy: 'network-only' }
    )


    const handlePaginationModelChange = (model: GridPaginationModel) => {
        setPaginationModel({ page: model.page, pageSize: model.pageSize })
    }

    const handleSortModelChange = (model: GridSortModel) => {
        setSortModel(model);
    };

    const toggleDrawer = (newOpen: boolean) => () => {
        setDrawerOpen(newOpen)
    };

    const openDrawerFor = (insight: Insight) => {
        setSelectedInsight(insight);
        setDrawerOpen(true);
        trackInsightDrawerOpened({
            brand_id: brandId ?? '',
            account_id: ctx.accountId,
            kpi: insightKpi ?? '',
            label_name: insight.labelName ?? '',
            label_type: (insight.rawLabelType ?? '') as any,
        }, ctx)
    };

    const shareInsight = (insight: Insight, source: ShareInsightSource) => {
        setDownloadInsight(insight);
        trackShareInsight({
            source,
            brand_id: brandId ?? '',
            account_id: ctx.accountId,
            kpi: insightKpi ?? '',
            label_name: insight.labelName ?? '',
            label_type: (insight.rawLabelType ?? '') as LabelType,
            label_lift: insight.labelLift ?? '',
        }, ctx)
    };

    const rowMenuInsight = () => insights.find((insight) => insight.id === rowMenu?.rowId);

    const handleRowSelectionModelChange = (model: GridRowSelectionModel) => {
        setRowSelectionModel(Array.isArray(model) ? model : [model]);
        if (model.length > 0) {
            const matchedInsight = insights.filter((insight) => insight.id === model[0])
            if (matchedInsight.length > 0) {
                setSelectedInsight(matchedInsight[0])
            } else {
                setSelectedInsight(null)
            }

        }
        setDrawerOpen(model.length > 0 && !!selectedInsight)
    };
    useEffect(() => {
        if (res.listInsightV2?.data) {
            const currencyType = (res.listInsightV2?.metadata.currencyType || "USD") as CurrencyType;
            setInsightsCurrencyType(currencyType);
            const roundDollarFormat = getDollarFormat(currencyType);
            setInsights(res.listInsightV2.data.map((row) => {
                return {
                    id: row.id!, labelName: row.labelName,
                    labelType: formatEnumString(row.labelType),
                    rawLabelType: row.labelType,
                    labelLift: row.labelLift ? `${Math.round(row.labelLift * 100)}%` : '-',
                    rateWhenAbsent: row.rateWhenAbsent ? serializeInsightRate(row.rateWhenAbsent, insightKpi as PrismaKPI, currencyType) : '-',
                    rateWhenPresent: row.rateWhenPresent ? serializeInsightRate(row.rateWhenPresent, insightKpi as PrismaKPI, currencyType) : '-',
                    adSpendAbsent: row.adSpendAbsent ? roundDollarFormat.format(row.adSpendAbsent) : roundDollarFormat.format(0),
                    adSpendPresent: row.adSpendPresent ? roundDollarFormat.format(row.adSpendPresent) : roundDollarFormat.format(0),
                    countPresent: row.numPresent ? row.numPresent.toLocaleString() : '0',
                    countAbsent: row.numAbsent ? row.numAbsent.toLocaleString() : '0',
                    contentUrls: row.contentUrls?.filter(url => url !== null) || null,
                    contentTexts: row.contentTexts?.filter(text => text !== null) || null,
                    metaAds: row.metaAds?.filter(adId => adId !== null) || null
                }
            }));
            setTotalCount(res.listInsightV2.count)
        }
    }, [res, insightKpi])

    const [labelLiftCache, setLabelLiftCache] = useState<Record<string, LabelLiftData[]>>({});

    useEffect(() => {
        if (!drawerOpen || !selectedInsight) return
        const cached = labelLiftCache[selectedInsight.id]
        console.log('[InsightsTable] drawer opened', {
            insightId: selectedInsight.id,
            insightsGraphEnabled,
            metricInsightGraphEnabled,
            labelLiftFromCache: !!cached,
            cachedSeriesLength: cached?.length,
        })
    }, [drawerOpen, selectedInsight, insightsGraphEnabled, metricInsightGraphEnabled, labelLiftCache])

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            description: 'Unique identifier for the label',
            width: 100,
            disableExport: true,
            sortable: false,
        },
        {
            field: 'labelName',
            headerName: 'Label',
            width: 300,
            hideable: false,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="Label"
                tooltip="Element or component part of the ad"
                />
            ),
            renderCell: (params: GridCellParams) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', height: '100%' }}>
                    <TextCell value={params.value as string} />
                    <RowActionsButton
                        active={rowMenu?.rowId === params.id}
                        onClick={(anchorEl) => setRowMenu({ rowId: params.id as string, anchorEl })}
                    />
                </div>
            ),
        },
        {
            field: 'labelType',
            headerName: 'Type',
            width: 150,
            hideable: false,
            sortable: true,
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
        {
            field: 'labelLift',
            headerName: 'Label Lift',
            width: 150,
            hideable: false,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="Label Lift"
                tooltip="Primary effectiveness metric calculated as a relative difference (% change) in the chosen KPI when the label is present in ads vs. when it is not"
                />
            ),
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
        {
            field: 'rateWhenPresent',
            headerName: 'KPI (present)',
            width: 150,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="KPI (present)"
                tooltip="Aggregate KPI value of all ads where label is present"
                />
            ),
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
        {
            field: 'rateWhenAbsent',
            headerName: 'KPI (absent)',
            width: 150,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="KPI (absent)"
                tooltip="Aggregate KPI value of all ads where label is absent"
                />
            ),
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
        {
            field: 'adSpendPresent',
            headerName: 'Spend (present)',
            width: 150,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="Spend (present)"
                tooltip="Total spend on all ads where label is present"
                />
            ),
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
        {
            field: 'adSpendAbsent',
            headerName: 'Spend (absent)',
            width: 150,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="Spend (absent)"
                tooltip="Total spend on all ads where label is absent"
                />
            ),
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
        {
            field: 'countPresent',
            headerName: '# of Ads (present)',
            width: 150,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="# of Ads (present)"
                tooltip="Total number of ads where label is present"
                />
            ),
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
        {
            field: 'countAbsent',
            headerName: '# of Ads (absent)',
            width: 150,
            sortable: true,
            renderHeader: () => (
                <HeaderWithTooltip
                label="# of Ads (absent)"
                tooltip="Total number of ads where label is absent"
                />
            ),
            renderCell: (params: GridCellParams) => (
            <TextCell value={params.value as string} />
            ),
        },
    ];

    function CustomToolbar() {
        const handleExportCSV = async () => {
            try {

                const exportRes = await fetchQuery<InsightsTableExportCSVQuery>(
                    environment,
                    graphql`
                    query InsightsTableExportCSVQuery($input: ListInsightV2Request!) {
                        exportCSV(input: $input) {
                            csvData
                        }
                    }
                    `, tableQueryVariables
                ).toPromise()

                if (exportRes && exportRes.exportCSV && exportRes.exportCSV.csvData) {
                    trackInsightsExported({ brand_id: brandId ?? '', account_id: ctx.accountId, kpi: insightKpi ?? '' }, ctx)
                    // Create a download link or file blob with the CSV data
                    const csvData = exportRes.exportCSV.csvData
                    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' })
                    const url = URL.createObjectURL(blob)

                    // Trigger the download
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', 'insights.csv')
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                }
            } catch (error) {
                console.error('Error exporting CSV:', error)
            }
        }

        return (
            <GridToolbarContainer sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <FilterBar.MetricFilter
                    useFilterIcon={true}
                    value={insightMetricsFilters}
                    handleChange={(insightMetricsFilters) =>
                        setParams({ insightMetricsFilters }, 'pushIn')
                    }
                    name="metrics"
                    renderOptionLabel={(opt) => getInsightHeaderName(opt)}
                    fieldOptions={[
                        "LabelLift",
                        "KpiPresent",
                        "KpiAbsent",
                        "SpendPresent",
                        "SpendAbsent",
                        "NumberOfAdsPresent",
                        "NumberOfAdsAbsent"
                    ]}
                />
                <CustomTooltip title="Export as CSV" placement="top">
                    <span>
                        <Button variant="blue" onClick={handleExportCSV}>
                        <FileDownloadOutlinedIcon />
                        </Button>
                    </span>
                </CustomTooltip>
            </GridToolbarContainer>)
    }
    return (
        <div className="flex items-center justify-between gap-6">
            <Drawer open={drawerOpen} onClose={toggleDrawer(false)} anchor='right' PaperProps={{ sx: { width: { xs: 'min(100vw - 16px, 560px)', sm: 440, lg: 600 }, maxWidth: '100vw' }}}>
            {selectedInsight && (
                labelLiftCache[selectedInsight.id] ? (
                <InsightDrawerWithCachedData
                    insight={selectedInsight}
                    kpi={insightKpi ? (insightKpi as KPI) : null}
                    kpiDisplay={insightKpi ? KpiDisplay[insightKpi as KPI] : 'KPI'}
                    currencyType={insightsCurrencyType}
                    insightsGraphEnabled={insightsGraphEnabled}
                    metricInsightGraphEnabled={metricInsightGraphEnabled}
                    cachedData={labelLiftCache[selectedInsight.id]}
                    onCloseDrawer={toggleDrawer(false)}
                    onShareInsight={() => shareInsight(selectedInsight, 'drawer')}
                />
                ) : (
                <Suspense fallback={<LoadingScreen />}>
                    <InsightDrawerWithQuery
                    insight={selectedInsight}
                    kpi={insightKpi ? (insightKpi as KPI) : null}
                    kpiDisplay={insightKpi ? KpiDisplay[insightKpi as KPI] : 'KPI'}
                    currencyType={insightsCurrencyType}
                    insightsGraphEnabled={insightsGraphEnabled}
                    metricInsightGraphEnabled={metricInsightGraphEnabled}
                    labelLiftCache={labelLiftCache}
                    setLabelLiftCache={setLabelLiftCache}
                    onCloseDrawer={toggleDrawer(false)}
                    onShareInsight={() => shareInsight(selectedInsight, 'drawer')}
                    />
                </Suspense>
                )
            )}
            </Drawer>

            <DataGrid
                rows={insights}
                columns={columns}
                sortModel={sortModel}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                disableColumnFilter
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                pageSizeOptions={[20, 50, 100]}
                rowCount={totalCount}
                onPaginationModelChange={handlePaginationModelChange}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                onRowClick={(params) => {
                    const matchedInsight = insights.find((insight) => insight.id === params.id);
                    if (matchedInsight) openDrawerFor(matchedInsight);
                }}
                slots={{ toolbar: CustomToolbar }}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'labelLift', sort: 'desc' }]
                    },
                    columns: {
                        columnVisibilityModel: {
                            // Id is an internal column
                            id: false,
                        },
                    },
                }}
                autoHeight={true}
                getRowHeight={(params: GridRowHeightParams) => ('auto')}
                sx={{
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                    },
                    '& .MuiDataGrid-row:hover .row-actions-button': {
                        opacity: 1,
                        pointerEvents: 'auto',
                    },
                }}
            />

            <RowActionsMenu
                anchorEl={rowMenu?.anchorEl ?? null}
                onClose={() => setRowMenu(null)}
                onExploreInsight={() => {
                    const matchedInsight = rowMenuInsight()
                    if (matchedInsight) openDrawerFor(matchedInsight)
                }}
                onDownloadInsightImage={() => {
                    const matchedInsight = rowMenuInsight()
                    if (matchedInsight) shareInsight(matchedInsight, 'row_menu')
                }}
            />

            {downloadInsight && (
                <DownloadInsightModal
                    open
                    onClose={() => setDownloadInsight(null)}
                    insight={downloadInsight}
                    brandId={brandId ?? ''}
                    kpi={insightKpi ?? ''}
                    workspaceName={workspaceName}
                    kpiDisplay={insightKpi ? KpiDisplay[insightKpi as KPI] : 'KPI'}
                    performanceDateGte={performanceDateGte}
                    performanceDateLte={performanceDateLte}
                />
            )}
        </div>
    )
};

export default InsightsTable;